import { Link } from "react-router-dom";
import { Clock, Users, Check, ArrowRight, Trophy, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { LOTTA, LOTTA_STORY, LOTTA_TITOLI, PATRIMONIO } from "@/data/site";

export default function Lotta() {
  return (
    <div data-testid="lotta-page">
      <PageHero
        image={LOTTA.hero}
        overline="Lotta Greco-Romana & Stile Libero"
        titleLines={["La", "Lotta"]}
        subtitle={LOTTA.intro}
      />

      {/* Storia della sezione lotta */}
      <section className="bg-white py-20 md:py-32" data-testid="lotta-storia">
        <div className="mx-auto grid max-w-[1400px] items-start gap-12 px-5 md:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <SectionHeading overline={LOTTA_STORY.eyebrow} title="Una tradizione azzurra" />
              <div className="mt-8 overflow-hidden rounded-2xl bg-cab-deep">
                <video
                  src="/videos/lotta-cab.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="aspect-[4/3] w-full object-cover"
                  data-testid="lotta-tradizione-video"
                />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-16">
            <div className="space-y-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              {LOTTA_STORY.paragraphs.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p>{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-cream py-20 md:py-32" data-testid="lotta-benefici">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Perché praticarla"
            title="I benefici della lotta"
            subtitle="Uno sport completo che allena il corpo e la mente, adatto a bambini, ragazzi e adulti."
          />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {LOTTA.benefits.map((b, i) => (
              <StaggerItem key={b.title}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                  <span className="font-display text-5xl font-extrabold tracking-tighter text-cab/25">
                    0{i + 1}
                  </span>
                  <h3 className="mt-3 font-display text-xl font-extrabold uppercase tracking-tight text-ink">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{b.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Titoli e medaglie di squadra */}
      <section className="bg-white py-20 md:py-32" data-testid="lotta-titoli">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Risultati collettivi"
            title="Titoli e medaglie di squadra"
            subtitle={LOTTA_TITOLI.intro}
          />
          <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 md:mt-16">
            {LOTTA_TITOLI.items.map((it) => (
              <StaggerItem key={it}>
                <div className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-cream p-6">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                    <Trophy className="h-6 w-6" />
                  </span>
                  <p className="font-display text-lg font-bold tracking-tight text-ink">{it}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
              {LOTTA_TITOLI.closing}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Un patrimonio sportivo e umano */}
      <section className="relative overflow-hidden bg-cab-deep py-20 md:py-28" data-testid="lotta-patrimonio">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading overline="Il medagliere" title={PATRIMONIO.title} subtitle={PATRIMONIO.intro} light />
          <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 md:mt-16">
            {PATRIMONIO.values.map((v) => (
              <StaggerItem key={v}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-white/15 bg-white/5 p-6">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-cab-light" />
                  <p className="font-medium text-cream">{v}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-3xl text-base leading-relaxed text-cream/80 md:text-lg">
              {PATRIMONIO.closing}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Schedule + coaches */}
      <section className="bg-white py-20 md:py-32" data-testid="lotta-orari">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading overline="Turni di allenamento" title="Orari dei corsi" />
            <div className="mt-10 overflow-hidden rounded-2xl border border-ink/10">
              {LOTTA.schedule.map((s, i) => (
                <Reveal key={s.group} delay={i * 0.05}>
                  <div className="flex items-center justify-between gap-4 border-b border-ink/10 bg-cream px-6 py-5 last:border-0">
                    <div>
                      <p className="font-display text-lg font-bold text-ink">{s.group}</p>
                      <p className="text-sm text-muted-foreground">{s.days}</p>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-md bg-cab/10 px-4 py-2 font-bold text-cab">
                      <Clock className="h-4 w-4" />
                      {s.time}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading overline="Il team tecnico" title="Gli istruttori" />
            <div className="mt-10 space-y-4">
              {LOTTA.coaches.map((c, i) => (
                <Reveal key={c.name} delay={i * 0.05}>
                  <div className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-cream p-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-cab text-cream">
                      <Users className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="font-display text-lg font-bold text-ink">{c.name}</p>
                      <p className="text-sm text-cab">{c.role}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-6 rounded-2xl bg-cab-deep p-7 text-cream">
                <div className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-cab-light" />
                  <p className="text-[15px] leading-relaxed text-cream/85">
                    Prima lezione di prova <strong className="text-cream">gratuita</strong> per tutti i nuovi
                    iscritti. Porta abbigliamento comodo, al resto pensiamo noi.
                  </p>
                </div>
                <Link
                  to="/dove-siamo"
                  data-testid="lotta-cta"
                  className="group mt-5 inline-flex items-center gap-2 rounded-md bg-cream px-6 py-3 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Prenota una prova
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="border-y border-ink/10 bg-cream py-6">
        <Marquee items={["Tecnica", "Equilibrio", "Forza", "Rispetto"]} outline />
      </div>

      {/* Gallery */}
      <section className="bg-cream py-20 md:py-32" data-testid="lotta-gallery">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading overline="In palestra" title="La galleria" />
          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {LOTTA.gallery.map((img, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="group overflow-hidden rounded-xl bg-cab-deep">
                  <img
                    src={img}
                    alt={`Lotta ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
